import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useProjetoImpressaoDisciplinaFilters } from "../hooks/Impressao_cadastro/filter";
import { ImpressaoProjetoModel } from "../../domain/models/impressao_projeto";
import { defaultProjetoImpressaoCadastroForm, ProjetoImpressaoCadastroForm, ProjetoImpressaoCadastroSchema } from "../schemas/Impressao_cadastro/ImpressaoCadastroSchema";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

export type Disciplinas = {
  idDisciplina: number;
  descricao: string;
}

type LoadDisciplinasSubdiciplinasFuncState = {
  idProjeto?: number,
  tipo?: number | null,
  prancha?: string | null,
  revisao?: string | null,
  descricao?: string | null,

};

export const useImpressaoCadastroSolicitarViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getProjetosDropDownUseCase,

    },
    projetos: {
      createProjetoImpressaoUseCase,
      getProjetoImpressaoByIdUseCase,
      getDisciplinasSubdisciplinasByProjetoUseCase,

    },

  } = useDependencyInjection();

  const location = useLocation();
  const navigate = useNavigate();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useProjetoImpressaoDisciplinaFilters();

  const { pushErrorNotification, pushInfoNotification, pushWarnNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [disciplinasCadastradas, setDisciplinasCadastradas] = useState<Disciplinas[]>([]);
  const [disciplinasNaoCadastradas, setDisciplinasNaoCadastradas] = useState<Disciplinas[]>([]);

  const [ddlProjetos, setDDlProjetos] = useState<DropDown[]>();
  const [selectedIdProjetoImpressao, setSelectedIdProjetoImpressao] = useState<number>();
  const [selectedProjetoImpressao, setSelectedProjetoImpressao] = useState<ImpressaoProjetoModel>();
  const [visualizar, setVisualizar] = useState(false);


  const hookForm = useForm<ProjetoImpressaoCadastroForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultProjetoImpressaoCadastroForm,
    resolver: zodResolver(ProjetoImpressaoCadastroSchema),
  });

  const selectedIdCentroCusto = hookForm.watch("idCentroCusto");
  const selectedIdProjeto = hookForm.watch("idProjeto");

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  //#region CARREGAMENTO 


  const loadDropdownProjetos = useCallback(async (idCentroCusto?: number) => {
    try {
      setLoading(true);
      const result = await getProjetosDropDownUseCase.execute(idCentroCusto);
      setDDlProjetos(result);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar projetos!"));
      setDDlProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [
    getProjetosDropDownUseCase,
    pushErrorNotification
  ]);


  const loadProjetoImpressao = useCallback(
    async (idProjetoImpressao: number) => {
      setLoading(true);
      try {
        const result = await getProjetoImpressaoByIdUseCase.execute(idProjetoImpressao);
        setSelectedProjetoImpressao(result);

        hookForm.setValue("idProjetoImpressao", result.idProjetoImpressao);
        hookForm.setValue("idProjeto", result.idProjeto);
        hookForm.setValue("idCentroCusto", result.idCentroCusto);
        hookForm.setValue("idFuncionarioSolicitante", result.idFuncionarioSolicitante);
        hookForm.setValue("idFuncionarioAprovacao", result.idFuncionarioAprovacao);
        hookForm.setValue("idFornecedor", result.idFornecedor);

        hookForm.setValue("dataSolicitacao", result.dataSolicitacao ? new Date(result.dataSolicitacao) : undefined);
        hookForm.setValue("dataAprovacao", result.dataAprovacao ? new Date(result.dataAprovacao) : undefined);

        hookForm.setValue("quantidade", result.quantidade);
        hookForm.setValue("cor", Number(result.cor) ?? 0);
        hookForm.setValue("tamanho", result.tamanho ?? undefined);

        hookForm.setValue("observacoesSolicitacao", result.observacoesSolicitacao ?? undefined);
        hookForm.setValue("status", result.status);
        hookForm.setValue("statusEmail", result.statusEmail);

        hookForm.setValue("disciplinasIds", result.disciplinasIds);


        await loadDropdownProjetos(result.idCentroCusto);

      } catch (e: unknown) {
        setErrorMessage(processError(e, "Falha ao carregar revisão")[0]);
      } finally {
        setLoading(false);
      }
    },
    [
      getProjetoImpressaoByIdUseCase,
      hookForm,
      loadDropdownProjetos,
    ],
  );

  const loadDisciplinasSubdisciplinas = useCallback(async (funcState: LoadDisciplinasSubdiciplinasFuncState) => {
    try {
      setLoading(true);
      const filteredTipo = evaluateParamValue(funcState?.tipo, filters.tipo);
      const filteredPrancha = evaluateParamValue(funcState?.prancha, filters.prancha);
      const filteredRevisao = evaluateParamValue(funcState?.revisao, filters.revisao);
      const filteredDescricao = evaluateParamValue(funcState?.descricao, filters.descricao);

      if (!selectedIdProjeto) {
        pushWarnNotification("Atenção", "Selecione um projeto para carregar as disciplinas/subdisciplinas!");
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas([]);
        return;
      }

      const data = await getDisciplinasSubdisciplinasByProjetoUseCase.execute(
        selectedIdProjeto,
        filteredTipo ?? 2,
        filteredPrancha,
        filteredRevisao,
        filteredDescricao

      );

      if (selectedProjetoImpressao) {
        const disciplinasIds = selectedProjetoImpressao.disciplinasIds ?? [];
        setDisciplinasCadastradas(data.filter(x => disciplinasIds.includes(x.idDisciplina)));
        setDisciplinasNaoCadastradas(data.filter(x => !disciplinasIds.includes(x.idDisciplina)));
      } else {
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas(data);
      }

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar disciplinas/subdisciplinas!"));
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    selectedIdProjeto,
    selectedProjetoImpressao,
    getDisciplinasSubdisciplinasByProjetoUseCase,
    pushWarnNotification,
    pushErrorNotification,
  ]
  );

  //#endregion




  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const visualizarParam = params.get("visualizar");
      setVisualizar(visualizarParam === "true");

      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }
      setSelectedIdProjetoImpressao(Number(id));
      await loadProjetoImpressao(Number(locationId));
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

    }
  });

  useEffect(() => {
    if (selectedIdCentroCusto) {
      (async () => {
        await loadDropdownProjetos(selectedIdCentroCusto);
      })();
    }

    if (selectedIdProjeto) {
      (async () => {
        await loadDisciplinasSubdisciplinas({ idProjeto: selectedIdProjeto, tipo: 2 });
      })();
    }

  }, [
    selectedIdProjeto,
    selectedIdCentroCusto,
    loadDropdownProjetos,
    loadDisciplinasSubdisciplinas
  ]);



  //#endregion



  //#region  CREATE


  const handleSubmitProjetoImpressao = useCallback(async (form: ProjetoImpressaoCadastroForm) => {
    try {
      setSaving(true);
      if (!selectedProjetoImpressao) {
        const result = await createProjetoImpressaoUseCase.execute(
          form.idProjeto,
          form.idCentroCusto,
          form.idFuncionarioSolicitante,
          form.dataSolicitacao ?? undefined,
          form.quantidade,
          form.cor,
          form.tamanho,
          form.observacoesSolicitacao,
          form.disciplinasIds
        );

        setSelectedIdProjetoImpressao(result);
        await loadProjetoImpressao(result);
        setVisualizar(true);

        navigate(`/Impressao_listasolicitar`);
        pushInfoNotification("Sucesso", "Salvo com sucesso");
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar revisão!")[0]);
    } finally {
      setSaving(false);
    }
  }, [
    selectedProjetoImpressao,
    createProjetoImpressaoUseCase,
    loadProjetoImpressao,
    navigate,
    pushInfoNotification
  ]);

  //#endregion



  //#region MUDANÇA DE PROJETOS


  const handleChangeDisciplinasLista = useCallback((src: Disciplinas[], selected: Disciplinas[]) => {
    setDisciplinasNaoCadastradas(src);
    setDisciplinasCadastradas(selected);

    hookForm.setValue('disciplinasIds', selected.map(x => x.idDisciplina));
  }, [
    hookForm,
  ]);


  //#endregion




  //#region PESQUISA DE DISCIPLINAS

  const handleTipoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: number | null) => {
      filters.setTipo(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ tipo: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  const handlePranchaChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setPrancha(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ prancha: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );


  const handleRevisaoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setRevisao(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ revisao: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  const handleDescricaoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setDescricao(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ descricao: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );


  const handleClearFilters = useDebouncedCallback(
    debouncedRef,
    () => {
      filters.clear();
      return async () => {
        try {
          setLoading(true);
          if (selectedIdProjeto) {
            await loadDisciplinasSubdisciplinas({ idProjeto: selectedIdProjeto, tipo: 2 });
          }
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    selectedIdProjeto,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  //#endregion



  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/Impressao_listagerenciar')
  }, [navigate]);


  const handleReloadPage = () => {
    hookForm.reset();
    const params = new URLSearchParams(location.search);
    const locationId = params.get("id");
    if (locationId) loadProjetoImpressao(Number(locationId));

  };

  //#endregion


  return {
    loading,
    selectedProjetoImpressao,
    disciplinasCadastradas,
    disciplinasNaoCadastradas,
    filters,
    visualizar,
    hookForm,
    errorMessage,
    ddlProjetos,
    saving,

    handleChangeDisciplinasLista,
    handleTipoChange,
    handlePranchaChange,
    handleRevisaoChange,
    handleDescricaoChange,
    handleClearFilters,
    handleSubmitProjetoImpressao: hookForm.handleSubmit(handleSubmitProjetoImpressao),
    handlePageBack,
    handleReloadPage,


  };
};
